import type { FieldSource } from '../../types';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/**
 * Form and layout primitives, styled to the supplied design screenshots.
 *
 * Every visual decision lives here so a design revision touches one file rather
 * than every screen. Tokens come from `tailwind.config.js`.
 */

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** The white rounded panel that holds a screen's content. */
export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('rounded-card border border-line bg-surface shadow-card', className)}>
      {children}
    </div>
  );
}

/**
 * A titled block within a card, e.g. "Record Details", "Campaign Setup".
 * `muted` renders the greyed-out treatment the design uses for Performance.
 */
export function Section({
  title,
  hint,
  muted = false,
  action,
  children,
  className,
}: {
  title: string;
  hint?: string;
  muted?: boolean;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('space-y-4', className)}>
      <div className="flex items-center justify-between gap-4">
        <h2
          className={cx(
            'text-section font-semibold',
            muted ? 'text-ink-disabled' : 'text-ink',
          )}
        >
          {title}
          {hint ? (
            <span className="ml-1.5 font-normal italic text-ink-muted">{hint}</span>
          ) : null}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** The two-column field grid used throughout the form. */
export function FieldGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('grid grid-cols-1 gap-x-8 gap-y-row sm:grid-cols-2', className)}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------

export interface FieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  /** Rendered under the control in small grey text. */
  help?: ReactNode;
  error?: string;
  /** Greys out the label, for the disabled Performance block. */
  muted?: boolean;
  /** Subtle "from brief" / "edited" chip. */
  source?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Field({
  label,
  htmlFor,
  required = false,
  hint,
  help,
  error,
  muted = false,
  source,
  children,
  className,
}: FieldProps) {
  return (
    <div className={cx('min-w-0', className)}>
      <div className="mb-1.5 flex items-baseline gap-2">
        <label
          htmlFor={htmlFor}
          className={cx('text-label', muted ? 'text-ink-disabled' : 'text-ink-soft')}
        >
          {label}
          {required ? <span className="text-danger">*</span> : null}
          {hint ? <span className="ml-1 italic text-ink-faint">{hint}</span> : null}
        </label>
        {source}
      </div>
      {children}
      {error ? (
        <p role="alert" className="mt-1.5 text-micro text-danger">
          {error}
        </p>
      ) : help ? (
        <p className="mt-1.5 text-micro text-ink-faint">{help}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

const CONTROL_BASE =
  'w-full rounded-input border bg-surface px-4 text-field text-ink placeholder:text-ink-faint ' +
  'transition-colors focus:border-accent focus:ring-2 focus:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:bg-surface disabled:text-ink-disabled ' +
  'disabled:placeholder:text-ink-disabled';

function controlClass(invalid?: boolean, extra?: string): string {
  return cx(CONTROL_BASE, invalid ? 'border-danger' : 'border-line', extra);
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ invalid, className, ...props }: InputProps) {
  return <input {...props} className={controlClass(invalid, cx('h-12', className))} />;
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function TextArea({ invalid, className, rows = 3, ...props }: TextAreaProps) {
  return (
    <textarea
      {...props}
      rows={rows}
      className={controlClass(invalid, cx('resize-y py-3 leading-relaxed', className))}
    />
  );
}

/** Chevron drawn inline so the select needs no icon dependency. */
function Chevron({ muted = false }: { muted?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={cx(
        'pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2',
        muted ? 'text-ink-disabled' : 'text-accent',
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 7.5 10 12.5 15 7.5" />
    </svg>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** Shown as a non-selectable first option, matching "Select Type". */
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
}

export function Select({
  invalid,
  placeholder,
  options,
  className,
  disabled,
  value,
  ...props
}: SelectProps) {
  const isPlaceholder = !value;

  return (
    <div className="relative">
      <select
        {...props}
        value={value}
        disabled={disabled}
        className={controlClass(
          invalid,
          cx(
            'h-12 cursor-pointer appearance-none pr-11',
            // The placeholder option is rendered in the lighter placeholder tone.
            isPlaceholder && !disabled ? 'text-ink-faint' : '',
            className,
          ),
        )}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} className="text-ink">
            {option.label}
          </option>
        ))}
      </select>
      <Chevron muted={disabled} />
    </div>
  );
}

/**
 * An input with a fixed grey prefix box, used for the currency fields.
 * The prefix is decorative, so it is hidden from assistive technology and the
 * unit is folded into the accessible label instead.
 */
export function PrefixInput({
  prefix,
  invalid,
  className,
  ...props
}: InputProps & { prefix: string }) {
  return (
    <div
      className={cx(
        'flex h-12 overflow-hidden rounded-input border bg-surface',
        invalid ? 'border-danger' : 'border-line',
        'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
      )}
    >
      <span
        aria-hidden="true"
        className="flex w-12 shrink-0 items-center justify-center border-r border-line bg-surface-muted text-field text-ink-muted"
      >
        {prefix}
      </span>
      <input
        {...props}
        className={cx(
          'no-spinner min-w-0 flex-1 bg-transparent px-4 text-field text-ink',
          'placeholder:text-ink-faint focus:outline-none',
          'disabled:cursor-not-allowed disabled:text-ink-disabled',
          className,
        )}
      />
    </div>
  );
}

/**
 * An input paired with a unit dropdown on its right, as used for
 * "Duration of Campaign" (number + Days/Weeks/Months).
 */
export function InputWithUnit({
  unitValue,
  unitOptions,
  onUnitChange,
  invalid,
  className,
  unitLabel,
  ...props
}: InputProps & {
  unitValue: string;
  unitOptions: Array<{ value: string; label: string }>;
  onUnitChange: (value: string) => void;
  unitLabel: string;
}) {
  return (
    <div
      className={cx(
        'flex h-12 overflow-hidden rounded-input border bg-surface',
        invalid ? 'border-danger' : 'border-line',
        'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
      )}
    >
      <input
        {...props}
        className={cx(
          'no-spinner min-w-0 flex-1 bg-transparent px-4 text-field text-ink',
          'placeholder:text-ink-faint focus:outline-none',
          className,
        )}
      />
      <div className="relative shrink-0">
        <select
          aria-label={unitLabel}
          value={unitValue}
          onChange={(event) => onUnitChange(event.target.value)}
          className="h-full cursor-pointer appearance-none border-l border-line bg-surface-muted pl-4 pr-9 text-field text-ink-muted focus:outline-none"
        >
          {unitOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Chevron />
      </div>
    </div>
  );
}

/** Square accent checkbox with a drawn tick, as used on the cohort list. */
export function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
  className,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible name; not rendered visually. */
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] border transition-colors',
        checked ? 'border-accent bg-accent' : 'border-line-strong bg-surface hover:border-accent',
        'focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-1',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {checked ? (
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="h-3 w-3 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
        </svg>
      ) : null}
    </button>
  );
}

/** Two-state segmented control, as used for Impression Auto / Manual. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex h-12 shrink-0 overflow-hidden rounded-input border border-line"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cx(
              'w-[76px] text-field transition-colors',
              active
                ? 'bg-surface font-medium text-ink shadow-segment-active'
                : 'bg-surface-muted text-ink-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A read-only field that looks like an input, for computed values. */
export function ReadonlyValue({
  value,
  placeholder = '-',
  className,
}: {
  value: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex h-12 items-center rounded-input border border-line bg-surface px-4 text-field',
        value ? 'text-ink' : 'text-ink-faint',
        className,
      )}
    >
      {value || placeholder}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'add' | 'danger';
  loading?: boolean;
}

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary: 'border border-line-strong bg-surface text-ink hover:bg-canvas',
  ghost: 'text-accent hover:text-accent-hover hover:underline',
  // The outlined blue "+ Add Cohort" / "+ Add Metric" button.
  add: 'border border-accent bg-surface text-accent hover:bg-accent-soft',
  danger: 'border border-line-strong bg-surface text-danger hover:bg-danger-soft',
};

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-button px-6 py-2.5 text-field font-medium',
        'transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'ghost' && 'px-0 py-0',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Spinner className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

/** Small "+ Label" button matching "+ Add Cohort" and "+ Add Metric". */
export function AddButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button variant="add" onClick={onClick} className="px-4 py-2 text-field font-normal">
      <span aria-hidden="true" className="text-base leading-none">
        +
      </span>
      {children}
    </Button>
  );
}

/** Icon-only remove control for repeatable rows. */
export function RemoveButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-button text-ink-faint transition-colors hover:bg-danger-soft hover:text-danger focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      <svg
        viewBox="0 0 20 20"
        aria-hidden="true"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      >
        <path d="M5 5l10 10M15 5L5 15" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
      className={cx('animate-spin', className)}
      fill="none"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The subtle source indicator. Kept to a small chip so it never competes with
 * the field content, per the design's restraint.
 *
 * `attachment` is called out separately from `brief`: a value read out of a
 * screenshot was interpreted rather than copied, and is the one the reviewer
 * most needs to check against the original.
 */
export function SourceChip({ source }: { source: FieldSource | undefined }) {
  if (!source) return null;

  const chips: Record<FieldSource, { label: string; title: string; className: string }> = {
    brief: {
      label: 'from brief',
      title: 'Extracted from the pasted brief',
      className: 'bg-accent-soft text-accent',
    },
    attachment: {
      label: 'from file',
      title: 'Read from an uploaded file — check it against the original',
      className: 'bg-warning-soft text-warning',
    },
    user: {
      label: 'edited',
      title: 'Added or edited by you',
      className: 'bg-surface-muted text-ink-muted',
    },
  };

  const chip = chips[source];
  return (
    <span
      title={chip.title}
      className={cx('rounded-full px-1.5 py-px text-[10px] font-medium leading-4', chip.className)}
    >
      {chip.label}
    </span>
  );
}

export type NoticeTone = 'info' | 'warning' | 'error' | 'success';

const NOTICE_TONES: Record<NoticeTone, string> = {
  info: 'border-accent/30 bg-accent-soft text-ink',
  warning: 'border-warning/25 bg-warning-soft text-ink',
  error: 'border-danger/25 bg-danger-soft text-ink',
  success: 'border-success/25 bg-success-soft text-ink',
};

export function Notice({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: NoticeTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={cx('rounded-input border px-4 py-3', NOTICE_TONES[tone])}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {title ? <p className="text-field font-medium">{title}</p> : null}
          {children ? <div className="text-micro text-ink-soft">{children}</div> : null}
        </div>
        {action}
      </div>
    </div>
  );
}

/** A label/value pair for the read-only summary screens. */
export function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-full shrink-0 text-label text-ink-muted sm:w-44">{label}</dt>
      <dd className="min-w-0 break-words text-field text-ink">{children}</dd>
    </div>
  );
}
